import { useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPageSEO, type SEOLanguage } from "@/data/seoTranslations";
import { getPathWithoutLanguage, getLocalizedUrl } from "@/utils/languageUtils";
import {
  BRG_CLAIM_EMAIL,
  getBestRateGuaranteeCopy,
} from "@/data/best-rate-guarantee-i18n";
import { cn } from "@/lib/utils";

const BestRateGuarantee = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const seoLang = language as SEOLanguage;
  const seoData = useMemo(() => getPageSEO("bestRateGuarantee", seoLang), [seoLang]);
  const copy = useMemo(() => getBestRateGuaranteeCopy(language), [language]);
  const canonicalPath = getPathWithoutLanguage(location.pathname);
  const bookHref = getLocalizedUrl("/book", language);

  const [openId, setOpenId] = useState<string | null>(null);

  const handleOpenChange = useCallback((id: string, next: boolean) => {
    setOpenId(next ? id : null);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-oid="brg-page-root">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={canonicalPath}
        language={seoLang}
      />
      <NewHeader lightBackground data-oid="brg-header" />

      <main
        className="mx-auto max-w-3xl px-5 pb-24 pt-28 sm:px-8 sm:pt-32 lg:pb-32"
        data-oid="brg-main"
      >
        <header className="border-b border-neutral-200 pb-10">
          <p className="font-lato text-[11px] font-semibold uppercase tracking-[0.28em] text-neutral-500">
            Beatenberg Resort &amp; Spa
          </p>
          <h1 className="mt-5 font-playfair text-3xl font-semibold tracking-tight text-neutral-900 sm:text-[2.125rem]">
            {copy.heroTitle}
          </h1>
          <p className="mt-6 font-lato text-[15px] leading-[1.65] text-neutral-700">
            {copy.heroLead}
          </p>
          <div className="mt-10 border-y border-neutral-200 py-6">
            <p className="font-lato text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">
              {copy.contactPrompt}
            </p>
            <a
              href={`mailto:${BRG_CLAIM_EMAIL}?subject=${encodeURIComponent(copy.emailSubject)}`}
              className="mt-2 inline-block font-lato text-[15px] font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[5px] transition-colors hover:decoration-neutral-900"
            >
              {BRG_CLAIM_EMAIL}
            </a>
            <p className="mt-5 font-lato text-sm leading-relaxed text-neutral-600">
              <a
                href={bookHref}
                className="font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
              >
                {copy.bookOfficialCta}
              </a>
              {copy.bookOfficialNote}
            </p>
          </div>
        </header>

        <div className="mt-10 space-y-5 font-lato text-[15px] leading-[1.65] text-neutral-700">
          {copy.intro.map((p, i) => (
            <p key={`intro-${i}`}>{p}</p>
          ))}
        </div>

        <div className="mt-14">
          {copy.sections.map((section, secIndex) => (
            <section
              key={section.id}
              id={section.id}
              className={cn("scroll-mt-28", secIndex > 0 && "mt-14")}
              aria-labelledby={`brg-${section.id}-title`}
            >
              <h2
                id={`brg-${section.id}-title`}
                className="border-t border-neutral-200 pt-8 font-lato text-base font-bold text-neutral-900"
              >
                {section.title}:
              </h2>

              <div className="border-b border-neutral-200">
                {section.faqs.map((faq, fq) => {
                  const id = `${section.id}-${fq}`;
                  const isOpen = openId === id;

                  return (
                    <Collapsible
                      key={id}
                      open={isOpen}
                      onOpenChange={(next) => handleOpenChange(id, next)}
                    >
                      <div className="border-t border-neutral-200">
                        <CollapsibleTrigger
                          className={cn(
                            "flex w-full items-start justify-between gap-6 py-5 text-left",
                            "font-playfair text-[13px] font-light uppercase leading-snug tracking-[0.1em] text-neutral-900",
                            "transition-colors hover:bg-neutral-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2",
                          )}
                        >
                          <span className="min-w-0 flex-1">{faq.question}</span>
                          <span
                            className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center font-lato text-xl font-extralight leading-none text-neutral-900"
                            aria-hidden
                          >
                            {isOpen ? "−" : "+"}
                          </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                          <div className="space-y-3 pb-6 pr-2 sm:pr-8">
                            {faq.answer.map((para, pi) => (
                              <p
                                key={`${id}-p-${pi}`}
                                className="font-lato text-sm leading-relaxed text-neutral-700"
                              >
                                {para}
                              </p>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer data-oid="brg-footer" />
    </div>
  );
};

export default BestRateGuarantee;
